import RoleSection from "@/components/final-preview/role-section";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import queries from "@/queries";
import { Skeleton } from "@/styles/ui/skeleton";
import { cn } from "@/lib/utils";
import { useDSDFiles } from "@/hooks/use-dsd-files";
import { getFormattedDateTimeKST } from "@/utils/time";
import { Clock8, User } from "lucide-react";
import CategoryIndexSection from "@/components/final-preview/category-index-section";
import FloatingCategoryIndex from "@/components/final-preview/floating-category-index";
import { useEffect, useRef, useState } from "react";
import useTablesTaggingStatus from "@/hooks/queries/use-tables-tagging-status";

const TextSkeleton = ({
  width,
  isLoading,
  children,
}: {
  width: string;
  isLoading: boolean;
  children: React.ReactNode;
}) => {
  return isLoading ? (
    <Skeleton
      className={cn("w-96 h-[1.4em] rounded-md bg-neutral-100", width)}
    />
  ) : (
    children
  );
};

const FinalPreview = ({
  headerAction,
}: {
  /** 헤더 우측 액션 슬롯(라이브에선 HTML 다운로드 버튼 주입, 다운로드 산출물에선 미주입). */
  headerAction?: React.ReactNode;
}) => {
  const { workbookId } = useParams();
  const { data: workbookCategories, isLoading: isWorkbookLoading } = useQuery({
    ...queries.workbook(workbookId!)._ctx.categories(),
    enabled: !!workbookId,
  });

  const { data: taggingStatusData } = useTablesTaggingStatus({ workbookId });

  const { data } = useDSDFiles();

  const workbookInfo = data?.find((workspace) => workspace.id === workbookId);

  const sortedCategories = [...(workbookCategories?.categories || [])].sort(
    (a, b) => a.category.localeCompare(b.category, "ko", { numeric: true }),
  );

  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const sectionsRef = useRef<Map<string, HTMLDivElement>>(new Map());

  const getMap = () => {
    if (!sectionsRef.current) {
      sectionsRef.current = new Map();
    }
    return sectionsRef.current;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveCategoryId(entry.target.id);
          } else if (
            entry.target.id === sortedCategories[0]?.id &&
            entry.boundingClientRect.top > 0
          ) {
            setActiveCategoryId(null);
          }
        });
      },
      {
        rootMargin: "-20% 0px -60% 0px",
        threshold: 0.2,
      },
    );

    const map = getMap();
    map.forEach((node) => {
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, [sortedCategories]);

  return (
    <div className="flex flex-col items-start gap-6 self-stretch h-full overflow-x-hidden px-7">
      <div className="flex max-w-[1440px] min-w-[1280px] mx-auto py-8 flex-col items-start gap-6 self-stretch border-b border-neutral-200">
        <div className="flex w-full items-start justify-between gap-4">
          <span className="title-2-bold text-primary">
            <TextSkeleton width="100%" isLoading={isWorkbookLoading}>
              {workbookInfo?.name}
            </TextSkeleton>
          </span>
          {headerAction}
        </div>
        <div className="flex flex-col items-start gap-4">
          <div className="flex items-end gap-2 self-stretch">
            <div className="flex w-7 h-7 justify-center items-center gap-2.5 rounded-full bg-neutral-100">
              <Clock8 className="w-4 h-4 shrink-0 aspect-square" />
            </div>
            <span className="heading-2-medium text-neutral-500">
              마지막 수정 일시&nbsp;
            </span>
            <span className="heading-2-medium text-primary">
              {workbookInfo?.updatedAt
                ? getFormattedDateTimeKST(workbookInfo.updatedAt)
                : ""}
            </span>
          </div>
          <div className="flex items-center gap-2 self-stretch">
            <div className="flex w-7 h-7 justify-center items-center gap-2.5 rounded-full bg-neutral-100">
              <User className="w-4 h-4 shrink-0 aspect-square" />
            </div>
            <span className="heading-2-medium text-neutral-500">
              담당자&nbsp;
            </span>
            <span className="heading-2-medium text-primary">
              {workbookInfo?.creator}
            </span>
          </div>
        </div>
      </div>
      <CategoryIndexSection
        categories={sortedCategories}
        taggingStatusData={taggingStatusData}
      />
      <FloatingCategoryIndex
        className={cn("transition-opacity duration-300", {
          "opacity-0 pointer-events-none": activeCategoryId === null,
          "opacity-100": activeCategoryId !== null,
        })}
        categories={sortedCategories}
        activeCategoryId={activeCategoryId}
        taggingStatusData={taggingStatusData}
      />
      <div className="flex flex-col max-w-[1440px] min-w-[1280px] mx-auto px-7 items-center gap-2.5 bg-white pb-[40vh]">
        <div className="flex flex-col items-end flex-1 w-full">
          {sortedCategories.map((category) => (
            <RoleSection
              category={category}
              key={category.id}
              ref={(node) => {
                const map = getMap();
                if (node) {
                  map.set(category.id, node);
                } else {
                  map.delete(category.id);
                }
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FinalPreview;
